import type { BillingStatusResponse, CreateBillingCheckoutResponse } from '@onflow/shared';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { FlowButton } from '../components/v2/FlowButton.js';
import { FlowStatus } from '../components/v2/FlowStatus.js';
import { useLogout } from '../hooks/useAuth.js';
import {
  useBillingStatus,
  useCancelBillingSubscription,
  useConfirmBillingCheckout,
  useCreateBillingCheckout,
} from '../hooks/useBilling.js';
import { api } from '../lib/api.js';

interface RazorpaySuccess {
  razorpay_payment_id: string;
  razorpay_subscription_id: string;
  razorpay_signature: string;
}

interface RazorpayFailure {
  error?: {
    code?: string;
    description?: string;
    reason?: string;
    source?: string;
    step?: string;
  };
}

interface RazorpayInstance {
  open(): void;
  on(event: 'payment.failed', callback: (failure: RazorpayFailure) => void): void;
}

declare global {
  interface Window {
    Razorpay?: new (options: Record<string, unknown>) => RazorpayInstance;
  }
}

export function Billing() {
  const status = useBillingStatus();
  const checkout = useCreateBillingCheckout();
  const confirm = useConfirmBillingCheckout();
  const cancel = useCancelBillingSubscription();
  const navigate = useNavigate();
  const logout = useLogout();
  const [message, setMessage] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  async function subscribe() {
    setMessage(null);
    try {
      const config = await checkout.mutateAsync();
      await loadCheckout();
      const instance = openCheckout(config, async (result) => {
        try {
          await confirm.mutateAsync({
            razorpayPaymentId: result.razorpay_payment_id,
            razorpaySubscriptionId: result.razorpay_subscription_id,
            razorpaySignature: result.razorpay_signature,
          });
          navigate('/dashboard', { replace: true });
        } catch {
          setMessage('Payment was received, but verification is still pending. Refresh shortly.');
        }
      });
      instance.on('payment.failed', (failure) => {
        const description = failure.error?.description?.trim();
        setMessage(
          description
            ? `Payment did not complete: ${description}. No access changed.`
            : 'Payment did not complete. No access changed.',
        );
      });
      instance.open();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Checkout could not be opened.');
    }
  }

  async function signOut() {
    await logout();
    window.location.assign('/login');
  }

  async function deleteAccount() {
    if (!window.confirm('Delete your OnFlow account and cancel the current subscription?')) return;
    setDeleting(true);
    setMessage(null);
    try {
      await api<void>('/me', { method: 'DELETE' });
      await logout();
      window.location.assign('/login');
    } catch (error) {
      setMessage(
        error instanceof Error ? error.message : 'Account deletion could not be completed.',
      );
      setDeleting(false);
    }
  }

  function resetCreatedCheckout() {
    if (
      !window.confirm(
        'Reset this unpaid checkout? Only continue if your payment app shows the attempt as failed and no money was debited.',
      )
    ) {
      return;
    }
    setMessage(null);
    cancel.mutate(
      { atCycleEnd: false },
      {
        onSuccess: () =>
          setMessage('The unpaid checkout was reset. You can now start a fresh subscription.'),
        onError: (error) =>
          setMessage(error instanceof Error ? error.message : 'Checkout could not be reset.'),
      },
    );
  }

  if (status.isLoading) {
    return (
      <main className="billing-shell">
        <p>Loading billing…</p>
      </main>
    );
  }
  if (status.isError || !status.data) {
    return (
      <main className="billing-shell">
        <p>Billing status is unavailable. Try again shortly.</p>
      </main>
    );
  }

  const data = status.data;
  const hasAccess = data.entitlement !== 'locked';
  const hasSubscription = Boolean(data.subscription);
  const busy = checkout.isPending || confirm.isPending || cancel.isPending || deleting;
  const periodEnd = data.subscription?.currentPeriodEnd
    ? formatBillingDate(data.subscription.currentPeriodEnd)
    : null;
  const endingAtCycleEnd = Boolean(data.subscription?.cancelAtCycleEnd);

  return (
    <main className="billing-shell">
      <header className="billing-header">
        <Link className="billing-wordmark" to="/dashboard">
          OnFlow<span>.</span>
        </Link>
        {hasAccess && (
          <Link className="billing-back" to="/dashboard">
            Back to tracking
          </Link>
        )}
      </header>

      <section className="billing-hero" aria-labelledby="billing-title">
        <p className="billing-eyebrow">ONFLOW MEMBERSHIP</p>
        <h1 id="billing-title">
          {data.entitlement === 'locked' ? 'Keep the plan moving.' : 'Your subscription.'}
        </h1>
        <p className="billing-copy">
          Targets, daily tracking, and the weekly read on whether the work is moving you.
        </p>
      </section>

      <section className="billing-card" aria-label="OnFlow monthly subscription">
        <div className="billing-card-topline">
          <div>
            <p className="billing-label">MONTHLY</p>
            <p className="billing-price">
              <span>₹</span>
              {formatRupees(data.product.amountPaise)}
              <small>/ month</small>
            </p>
          </div>
          <BillingState status={data} />
        </div>

        <div className="billing-rule" />
        <dl className="billing-facts">
          <div>
            <dt>Trial</dt>
            <dd>{data.trialDays} days after setup</dd>
          </div>
          <div>
            <dt>{endingAtCycleEnd ? 'Access ends' : 'Next renewal'}</dt>
            <dd>
              {periodEnd ?? (hasSubscription ? 'Pending from Razorpay' : 'After subscribing')}
            </dd>
          </div>
          <div>
            <dt>Cancel</dt>
            <dd>{endingAtCycleEnd ? 'Scheduled · no further renewal' : 'Before the next cycle'}</dd>
          </div>
        </dl>

        {endingAtCycleEnd && (
          <div className="billing-alert" role="status">
            Your subscription is cancelled. You keep full access
            {periodEnd ? ` through ${periodEnd}` : ' through the current billing cycle'}, and you
            won&apos;t be charged again.
          </div>
        )}

        {message && (
          <div className="billing-alert" role="alert">
            {message}
          </div>
        )}

        {!hasSubscription ||
        data.subscription?.providerStatus === 'created' ||
        data.entitlement === 'locked' ? (
          <FlowButton
            block
            size="lg"
            onClick={() => void subscribe()}
            disabled={busy || !data.billingEnabled}
            data-testid="billing-subscribe"
          >
            {busy ? 'Opening secure checkout…' : 'Subscribe with Razorpay'}
          </FlowButton>
        ) : null}

        {data.subscription?.providerStatus === 'created' ? (
          <FlowButton
            block
            variant="quiet"
            onClick={resetCreatedCheckout}
            disabled={busy}
            data-testid="billing-reset-checkout"
            style={{ marginTop: 12 }}
          >
            Payment stuck? Reset checkout
          </FlowButton>
        ) : null}

        {data.subscription &&
        ['active', 'authenticated', 'pending'].includes(data.subscription.providerStatus) &&
        !data.subscription.cancelAtCycleEnd ? (
          <FlowButton
            block
            variant="quiet"
            onClick={() => {
              setMessage(null);
              cancel.mutate(
                { atCycleEnd: true },
                {
                  onError: (error) =>
                    setMessage(
                      error instanceof Error
                        ? error.message
                        : 'Cancellation could not be scheduled.',
                    ),
                },
              );
            }}
            disabled={busy}
            data-testid="billing-cancel"
            style={{ marginTop: 12 }}
          >
            Cancel at cycle end
          </FlowButton>
        ) : null}

        <p className="billing-secure-copy">
          Payment details stay with Razorpay. OnFlow never receives your card, UPI, or bank
          credentials.
        </p>
      </section>

      <section className="billing-recovery" aria-label="Account options">
        <button type="button" onClick={() => void signOut()} disabled={busy}>
          Sign out
        </button>
        <a href="mailto:support@onflow.fit?subject=OnFlow%20billing%20support">Billing support</a>
        <button type="button" onClick={() => void deleteAccount()} disabled={busy}>
          {deleting ? 'Deleting…' : 'Delete account'}
        </button>
      </section>
    </main>
  );
}

function BillingState({ status }: { status: BillingStatusResponse }) {
  if (!status.billingEnabled) return <FlowStatus state="neutral" label="Preview" />;
  if (status.entitlement === 'trialing') {
    return <FlowStatus state="drifting" label={`${status.daysRemaining}d trial`} />;
  }
  if (status.entitlement === 'active') {
    return (
      <FlowStatus
        state="on-track"
        label={status.subscription?.cancelAtCycleEnd ? 'Ends this cycle' : 'Active'}
      />
    );
  }
  if (status.entitlement === 'grace') return <FlowStatus state="drifting" label="Payment retry" />;
  return <FlowStatus state="off-track" label="Trial ended" />;
}

function formatBillingDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'Asia/Kolkata',
  });
}

function openCheckout(
  config: CreateBillingCheckoutResponse,
  handler: (result: RazorpaySuccess) => void,
): RazorpayInstance {
  if (!window.Razorpay) throw new Error('Secure checkout did not load');
  return new window.Razorpay({
    key: config.keyId,
    subscription_id: config.subscriptionId,
    name: config.productName,
    description: config.description,
    prefill: config.prefill,
    theme: { color: '#ee6b33' },
    handler,
    modal: { confirm_close: true },
  });
}

async function loadCheckout(): Promise<void> {
  if (window.Razorpay) return;
  await new Promise<void>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>('script[data-onflow-razorpay]');
    if (existing) {
      existing.addEventListener('load', () => resolve(), { once: true });
      existing.addEventListener(
        'error',
        () => reject(new Error('Secure checkout failed to load')),
        { once: true },
      );
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    script.dataset.onflowRazorpay = 'true';
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Secure checkout failed to load'));
    document.head.appendChild(script);
  });
}

function formatRupees(amountPaise: number): string {
  return new Intl.NumberFormat('en-IN', { maximumFractionDigits: 2 }).format(amountPaise / 100);
}
